/**
 * RNG determinístico do Rabbit 2.0 (regra A2, doc 06).
 * xoshiro128** com seed via splitmix32 — apenas operações inteiras (>>> | << ^ + *),
 * idênticas em qualquer engine JS (Node/browser). PROIBIDO Math.random na sim.
 */

export interface Rng {
  /** inteiro uniforme em [0, 2^32) */
  nextU32(): number;
  /** float uniforme em [0, 1) com 23 bits — determinístico */
  next01(): number;
  /** inteiro uniforme em [min, max] inclusivo */
  int(min: number, max: number): number;
  /** clona o estado atual (para forks de subsistema) */
  fork(streamId: number): Rng;
  /** estado serializável (retomada de run / replay) */
  state(): readonly [number, number, number, number];
}

function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    return (z ^ (z >>> 15)) >>> 0;
  };
}

export function createRng(seed: number, streamId = 0): Rng {
  const mix = splitmix32((seed ^ Math.imul(streamId + 1, 0x9e3779b1)) >>> 0);
  let s0 = mix(), s1 = mix(), s2 = mix(), s3 = mix();
  // evita estado todo-zero (inválido para xoshiro)
  if ((s0 | s1 | s2 | s3) === 0) s0 = 1;

  const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

  function nextU32(): number {
    const result = (Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9)) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotl(s3, 11);
    return result;
  }

  return {
    nextU32,
    next01: () => (nextU32() >>> 9) / 0x7fffff / 1.0000001192092896 % 1, // 23 bits → [0,1)
    int(min: number, max: number): number {
      const span = (max - min + 1) >>> 0;
      return min + (nextU32() % span);
    },
    fork: (id: number) => createRng((s0 ^ nextU32()) >>> 0, id),
    state: () => [s0, s1, s2, s3] as const,
  };
}
